import { getDeviceInfo } from "@/lib/device-detection";

export const BANNER_APP_FLAG = "BannerApp";

/** Rota pública de download inteligente (resolve iOS/Android). Divulgue: https://sysigreja.com/app */
export const APP_DOWNLOAD_PATH = "/app";

export const APP_CTA_TITLE = "SysIgreja no app";
export const APP_CTA_SUBTITLE = "Eventos, avisos e sua igreja no celular";

/** Path estático da arte OG (1200×630). Em DEV o Vite serve; em prod só após o deploy. */
export const APP_DOWNLOAD_OG_IMAGE_PATH = "/mobile-app/og-app.jpg";

/** OG/WhatsApp: HTTPS absoluto. Crawler não aceita localhost. */
export const APP_DOWNLOAD_OG_IMAGE = `https://sysigreja.com${APP_DOWNLOAD_OG_IMAGE_PATH}`;

export function resolveAppDownloadOgImageSrc(): string {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    return `${window.location.origin}${APP_DOWNLOAD_OG_IMAGE_PATH}`;
  }
  return APP_DOWNLOAD_OG_IMAGE;
}
export const APP_DOWNLOAD_OG_TITLE_FALLBACK = "Sua igreja";
export const APP_DOWNLOAD_OG_DESCRIPTION =
  "Bíblia, Devocional e eventos da sua comunidade. Baixe o app SysIgreja.";
export const APP_SALA_OG_DESCRIPTION =
  "Abra o app para ver Bíblia, Devocional e eventos da comunidade.";

/** `Sua igreja no celular` ou `{Nome da igreja} no celular`. */
export function buildAppDownloadOgTitle(igrejaNome?: string | null): string {
  const nome = igrejaNome?.trim();
  return `${nome || APP_DOWNLOAD_OG_TITLE_FALLBACK} no celular`;
}

export const APP_DOWNLOAD_OG_TITLE = buildAppDownloadOgTitle();
export const APP_SALA_OG_TITLE = APP_DOWNLOAD_OG_TITLE;

export function buildAppLandingPath(identificador?: string | null): string {
  const id = identificador?.trim();
  return id ? `${APP_DOWNLOAD_PATH}/${encodeURIComponent(id)}` : APP_DOWNLOAD_PATH;
}

/** Ícone do app SysIgreja para exibir na tela (192 px; o `/icon.png` de 1024 px pesa ~70 KB). */
export const DEFAULT_MOBILE_APP_ICON = "/ios/192.png";

export function resolveAppIconSrc(): string {
  const custom = (import.meta.env.VITE_MOBILE_APP_ICON_URL as string | undefined)?.trim();
  return custom || DEFAULT_MOBILE_APP_ICON;
}

/** Print real da home do app SysIgreja para vitrine no site. */
export const DEFAULT_MOBILE_APP_SHOWCASE_IMAGE =
  "/mobile-app/app-home-screen.jpg";

/** Mesma home, usada na moldura iOS do SiteAppPhoneMock. */
export const DEFAULT_MOBILE_APP_SHOWCASE_SCREEN_IMAGE =
  "/mobile-app/app-home-screen.jpg";

/** Badge oficial “Disponível no Google Play” (pt-BR). */
export const GOOGLE_PLAY_BADGE_PT_BR = "/mobile-app/google-play-badge-pt-br.png";

/** Badge oficial “Baixar na App Store” (pt-BR). */
export const APP_STORE_BADGE_PT_BR = "/mobile-app/app-store-badge-pt-br.svg";

/**
 * Fração da altura do PNG oficial do Play que é conteúdo (sem padding transparente).
 * Usar h_apple / PLAY_BADGE_CONTENT_RATIO para alinhar as pílulas.
 */
export const PLAY_BADGE_CONTENT_RATIO = 0.768;

const ANDROID_PACKAGE = "com.sysigreja.mobile";
const IOS_APP_STORE_ID = "6785946962";

export type MobilePlatform = "ios" | "android" | "other";
export type StoreBadgePlatform = "ios" | "android";

export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

export function getMobilePlatformFromUa(
  ua: string,
  opts?: { maxTouchPoints?: number; platform?: string },
): MobilePlatform {
  const maxTouchPoints = opts?.maxTouchPoints ?? 0;
  const platform = opts?.platform ?? "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && maxTouchPoints > 1);

  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export function getMobilePlatform(): MobilePlatform {
  if (typeof navigator === "undefined") return "other";
  return getMobilePlatformFromUa(navigator.userAgent, {
    maxTouchPoints: navigator.maxTouchPoints,
    platform: navigator.platform,
  });
}

/** Query de QA: força CTA mobile no desktop (`?simularApp=1`). */
export function isSimulatingMobileAppCta(): boolean {
  if (typeof window === "undefined") return false;
  const value = new URLSearchParams(window.location.search).get("simularApp");
  return value === "1" || value === "true";
}

/** Página carregada dentro do WebView do app nativo (React Native, in-app browser, etc.). */
export function isInsideNativeAppWebView(): boolean {
  return getDeviceInfo().isWebView;
}

/** Exibe CTA de loja nativa: celular real ou simulação via query (exceto WebView do app). */
export function shouldShowMobileAppCta(): boolean {
  if (isInsideNativeAppWebView()) return false;
  return isSimulatingMobileAppCta() || isMobileDevice();
}

/**
 * Plataforma efetiva para badges/banner.
 * Respeita `?platform=` / `?p=` e, em simulação no desktop, assume Android.
 */
export function getEffectiveMobilePlatform(): MobilePlatform {
  if (typeof window === "undefined") return getMobilePlatform();
  const params = new URLSearchParams(window.location.search);
  const forced =
    parseStorePlatformParam(params.get("platform")) ??
    parseStorePlatformParam(params.get("p"));
  if (forced) return forced;
  const detected = getMobilePlatform();
  if (isSimulatingMobileAppCta() && detected === "other") return "android";
  return detected;
}

function readEnvUrl(key: string): string | undefined {
  const value = (import.meta.env[key] as string | undefined)?.trim();
  return value || undefined;
}

const DEFAULT_IOS_APP_STORE_URL = `https://apps.apple.com/br/app/sysigreja/id${IOS_APP_STORE_ID}`;
const DEFAULT_ANDROID_PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}&hl=pt_BR&gl=BR`;

export function getIosAppStoreUrl(): string {
  return readEnvUrl("VITE_IOS_APP_STORE_URL") ?? DEFAULT_IOS_APP_STORE_URL;
}

export function getAndroidPlayStoreUrl(): string {
  return readEnvUrl("VITE_ANDROID_PLAY_STORE_URL") ?? DEFAULT_ANDROID_PLAY_STORE_URL;
}

export function getStoreUrlForPlatform(platform: MobilePlatform): string | null {
  if (platform === "ios") return getIosAppStoreUrl();
  if (platform === "android") return getAndroidPlayStoreUrl();
  return null;
}

function appendAndroidPlayReferrer(storeUrl: string, identificador: string): string {
  try {
    const url = new URL(storeUrl);
    const referrer = new URLSearchParams({
      identificador,
      utm_source: "site_igreja",
      utm_medium: "app_download",
    }).toString();
    url.searchParams.set("referrer", referrer);
    return url.toString();
  } catch {
    return storeUrl;
  }
}

/**
 * URL de download com igreja embutida quando possível.
 * - Android: Play Store + install referrer (`identificador`)
 * - iOS: App Store padrão (Apple não permite deferred gratuito)
 */
export function buildAppDownloadHref(opts?: {
  identificador?: string | null;
  platform?: MobilePlatform | StoreBadgePlatform;
}): string {
  const identificador = opts?.identificador?.trim() || undefined;
  const platform = opts?.platform ?? getMobilePlatform();

  if (platform === "android") {
    const androidUrl = getAndroidPlayStoreUrl();
    return identificador
      ? appendAndroidPlayReferrer(androidUrl, identificador)
      : androidUrl;
  }
  if (platform === "ios") {
    return getIosAppStoreUrl();
  }
  // Desktop: mostra ambas as lojas nos badges; href genérico usa Android com referrer se houver org
  if (identificador) {
    return appendAndroidPlayReferrer(getAndroidPlayStoreUrl(), identificador);
  }
  return getIosAppStoreUrl();
}

export function parseOrgIdentificadorParam(
  value: string | null | undefined,
): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

/** Badges a exibir: no celular só a loja correta; no desktop/outros, as duas. */
export function getVisibleStoreBadges(
  platform: MobilePlatform = getMobilePlatform(),
): StoreBadgePlatform[] {
  if (platform === "ios") return ["ios"];
  if (platform === "android") return ["android"];
  return ["ios", "android"];
}

export function getStoreLabelForPlatform(platform: MobilePlatform): string {
  if (platform === "ios") return "Baixar na App Store";
  if (platform === "android") return "Baixar no Google Play";
  return "Baixar o app";
}

export function parseStorePlatformParam(
  value: string | null | undefined,
): MobilePlatform | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "ios" || normalized === "iphone" || normalized === "ipad") {
    return "ios";
  }
  if (normalized === "android") return "android";
  if (normalized === "other" || normalized === "desktop" || normalized === "web") {
    return "other";
  }
  return null;
}

/** Scheme nativo: abre a igreja no app (`sysigreja://igreja/{identificador}`). */
export function buildNativeAppSchemeUrl(identificador: string): string {
  return `sysigreja://igreja/${encodeURIComponent(identificador.trim())}`;
}

const APP_OPEN_FALLBACK_MS = 1600;

export type OpenNativeAppOrStoreResult = "app_attempt" | "store_only";

/**
 * Tenta abrir o app nativo; se não abrir (timeout / página visível), vai para a loja.
 * Sem `nativeScheme` nem `identificador`, abre a loja direto.
 */
export function openNativeAppOrStore(opts: {
  identificador?: string | null;
  /** Scheme completo (ex.: `sysigreja://devocional/88`). Tem prioridade sobre `identificador`. */
  nativeScheme?: string | null;
  storeUrl: string;
  onAppAttempt?: () => void;
  onStoreFallback?: () => void;
}): OpenNativeAppOrStoreResult {
  if (typeof window === "undefined") return "store_only";

  const identificador = opts.identificador?.trim();
  const scheme = opts.nativeScheme?.trim() || (identificador ? buildNativeAppSchemeUrl(identificador) : "");
  if (!scheme) {
    window.open(opts.storeUrl, "_blank", "noopener,noreferrer");
    opts.onStoreFallback?.();
    return "store_only";
  }

  let appLikelyOpened = false;
  const markAppOpened = () => {
    appLikelyOpened = true;
  };

  document.addEventListener("visibilitychange", markAppOpened);
  window.addEventListener("pagehide", markAppOpened);
  window.addEventListener("blur", markAppOpened);

  opts.onAppAttempt?.();

  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = scheme;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", markAppOpened);
    window.removeEventListener("pagehide", markAppOpened);
    window.removeEventListener("blur", markAppOpened);
    try {
      document.body.removeChild(iframe);
    } catch {
      /* ignore */
    }

    if (!appLikelyOpened && !document.hidden) {
      opts.onStoreFallback?.();
      window.open(opts.storeUrl, "_blank", "noopener,noreferrer");
    }
  }, APP_OPEN_FALLBACK_MS);

  return "app_attempt";
}
